import { Buffer } from 'buffer'
export type CompressFormat = 'png' | 'jpeg' | 'jpg'

const atob = (str: string) => Buffer.from(str, 'base64').toString('binary')

export function imageResize(
  file: File,
  maxWidth: number,
  maxHeight: number,
  compressFormat: CompressFormat,
  quality: number,
  maxSize = 8000
): Promise<File> {
  return new Promise((res, rej) => {
    try {
      const reader = new FileReader()
      if (maxSize < file.size || !isCommonFormat(file)) {
        if (file.type && !file.type.includes('image')) {
          throw Error('File Is NOT Image!')
        } else {
          reader.readAsDataURL(file)
          reader.onload = () => {
            const image = new Image()
            ;(image as any).src = reader.result
            image.onload = function () {
              const resizedDataUrl = createDataURL(
                image as any,
                maxWidth,
                maxHeight,
                compressFormat,
                quality
              )

              const fileName = file.name
              const fileNameWithoutFormat = fileName
                .toString()
                .replace(/(png|jpeg|jpg|webp)$/i, '')
              const newFileName = fileNameWithoutFormat.concat(
                compressFormat.toString()
              )
              const newFile = base64toFile(
                resizedDataUrl,
                newFileName,
                compressFormat
              )
              res(newFile)
            }
          }
          reader.onerror = (error) => {
            rej(error)
            throw Error(error as any)
          }
        }
      } else if (maxSize >= file.size) {
        res(file)
      } else {
        throw Error('File Not Found!')
      }
    } catch (e: unknown) {
      rej(e)
    }
  })
}

function isCommonFormat(file: File) {
  return new RegExp(/png|jpg|jpeg/).test(file.type)
}

function base64toByteArrays(b64Data: any) {
  const sliceSize = 512

  const byteCharacters = atob(
    b64Data.toString().replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, '')
  )
  const byteArrays: any = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    // TODO - fix this
    //@ts-ignore
    byteArrays.push(byteArray)
  }
  return byteArrays
}

function base64toFile(
  b64Data: string,
  fileName: string,
  compressFormat: CompressFormat
) {
  const contentType = `image/${compressFormat}`
  const byteArrays = base64toByteArrays(b64Data)
  return new File(byteArrays, fileName, {
    type: contentType,
    lastModified: new Date().getTime(),
  })
}

function createDataURL(
  image: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  compressFormat: CompressFormat,
  quality: number
) {
  const qualityDecimal = quality / 100
  const canvas = document.createElement('canvas')

  let width = image.width
  let height = image.height

  const newHeightWidth = changeHeightWidth(height, maxHeight, width, maxWidth)

  canvas.width = newHeightWidth.width
  canvas.height = newHeightWidth.height

  width = newHeightWidth.width
  height = newHeightWidth.height

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
  ctx.fillStyle = 'rgba(0, 0, 0, 0)'
  ctx.fillRect(0, 0, width, height)

  ctx.drawImage(image, 0, 0, width, height)

  return canvas.toDataURL(`image/${compressFormat}`, qualityDecimal)
}

function changeHeightWidth(
  height: number,
  maxHeight: number,
  width: number,
  maxWidth: number
) {
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width)
    width = maxWidth
  }
  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height)
    height = maxHeight
  }
  return { height, width }
}
